import { motion } from "framer-motion";
import { useRef, useEffect } from "react";
import GlobalHeader from "@/components/GlobalHeader";
import Footer from "@/components/Footer";
import { PHOTOS, getPhotoUrl } from "@/lib/photos";
import { useMobileOnceInView } from "@/hooks/use-mobile-once-in-view";
import { usePageSEO } from "@/hooks/use-page-seo";
import {
  Calendar,
  Clock,
  MapPin,
  ArrowRight,
} from "lucide-react";

const posterImage = "/mgh-poster.jpg";

// Silent auction items — SEO-named WebP files hosted on Firebase Storage
// (full-size + _mobile variants, same convention as the photo library).
const AUCTION_FILE_PREFIX = "yacht-rock-detroit-music-giving-hope-charity-event-auction";

const auctionItems = [
  {
    name: "Jackson 5",
    item: "Authentic Jackson 5 tour guitar — the band that started it all for Music Giving Hope",
    file: "jackson-5-tour-guitar",
  },
  {
    name: "Foreigner",
    item: "Tour-used guitar personally signed by the members of Foreigner",
    file: "foreigner-signed-guitar",
  },
  {
    name: "Styx",
    item: "Guitar signed by the members of Styx, with signed Live in Concert poster",
    file: "styx-signed-guitar",
  },
  {
    name: "Todd Rundgren",
    item: "Jackson guitar signed on tour, with framed tour poster and all-access pass",
    file: "todd-rundgren-signed-guitar",
  },
  {
    name: "Rick Springfield",
    item: "Hand-signed Epiphone acoustic guitar with framed show poster",
    file: "rick-springfield-signed-guitar",
  },
].map((a) => ({
  ...a,
  full: getPhotoUrl(`${AUCTION_FILE_PREFIX}-${a.file}.webp`),
  preview: getPhotoUrl(`${AUCTION_FILE_PREFIX}-${a.file}_mobile.webp`),
}));

// Event structured data — helps this page surface in Google's event listings,
// rich results, and AI answers for "Detroit charity gala / concert July 2026".
const EVENT_JSONLD = {
  "@context": "https://schema.org",
  "@type": "MusicEvent",
  name: "Yacht Rock Detroit Unplugged — A Music Giving Hope Charity Gala",
  description:
    "An exclusive charity gala benefiting the Detroit Youth Choir, presented by Music Giving Hope. Acoustic yacht rock from Peter Beckett (Player), John Ford Coley, and Walter Egan, with cocktails, hors d'oeuvres, and a silent auction on the Detroit River.",
  startDate: "2026-07-16T18:00:00-04:00",
  endDate: "2026-07-16T21:30:00-04:00",
  eventStatus: "https://schema.org/EventScheduled",
  eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
  image: [
    "https://yachtrockdetroit.com/mgh-poster.jpg",
    ...auctionItems.map((a) => a.full),
  ],
  location: {
    "@type": "Place",
    name: "The Roostertail",
    address: {
      "@type": "PostalAddress",
      streetAddress: "100 Marquette Drive",
      addressLocality: "Detroit",
      addressRegion: "MI",
      postalCode: "48214",
      addressCountry: "US",
    },
  },
  performer: [
    { "@type": "MusicGroup", name: "Peter Beckett" },
    { "@type": "Person", name: "John Ford Coley" },
    { "@type": "Person", name: "Walter Egan" },
  ],
  organizer: {
    "@type": "Organization",
    name: "Music Giving Hope",
    url: "https://yachtrockdetroit.com/music-giving-hope",
  },
  offers: {
    "@type": "Offer",
    price: "100",
    priceCurrency: "USD",
    availability: "https://schema.org/InStock",
    url: "https://yachtrockdetroit.com/music-giving-hope",
    validFrom: "2026-06-01T00:00:00-04:00",
  },
};

const performers = [
  {
    name: "Peter Beckett",
    band: "The voice of Player",
    song: "Baby Come Back",
    image: "/artists/peter-beckett.jpg",
    blurb:
      "The #1 hit that became a yacht rock standard. Live and stripped down on the Detroit River.",
  },
  {
    name: "John Ford Coley",
    band: "England Dan & John Ford Coley",
    song: "I'd Really Love to See You Tonight",
    image: "/artists/john-ford-coley.jpg",
    blurb:
      "Half of the Grammy-nominated soft-rock duo. Those unmistakable harmonies, live and acoustic on the Detroit River.",
  },
  {
    name: "Walter Egan",
    band: "",
    song: "Magnet and Steel",
    image: "/artists/walter-egan.jpg",
    blurb:
      "Crafted with Stevie Nicks and Lindsey Buckingham. A yacht rock essential, up close and acoustic.",
  },
];

const timeline = [
  {
    time: "6:00 PM",
    title: "Red Carpet Arrivals & Cocktail Mixer",
    detail:
      "Silent auction opens. Drinks, coastal-style hors d'oeuvres, and mingling on the Detroit River.",
  },
  {
    time: "6:45 PM",
    title: "Welcome & Opening Remarks",
    detail: "A brief message about the night, the beneficiaries, and the sponsors making it possible.",
  },
  { time: "7:00 PM", title: "Opening Acoustic Set", detail: "The first of five intimate live performances begins." },
  { time: "7:25 PM", title: "Performance Two", detail: "A second acoustic set on the Roostertail's waterfront stage." },
  { time: "7:50 PM", title: "Performance Three", detail: "Mid-evening set. The auction heats up, the river lights up." },
  { time: "8:15 PM", title: "Performance Four", detail: "Another stripped-down set from the night's yacht rock legends." },
  {
    time: "8:35 PM",
    title: "Detroit Youth Choir Spotlight & Donation Appeal",
    detail: "The reason we're all here — a spotlight on the Detroit Youth Choir and a live call to give.",
  },
  { time: "8:40 PM", title: "Finale Set", detail: "The last performance of the night. The one you'll be talking about all weekend." },
  {
    time: "9:00 PM",
    title: "Silent Auction Close",
    detail: "Winning bids announced and packages awarded.",
  },
  {
    time: "9:20 PM",
    title: "Closing Remarks & VIP Networking",
    detail: "Final thank-yous and a preview of Yacht Rock Detroit weekend. The bar stays warm.",
  },
];

const includes = [
  "Five live acoustic performances from yacht rock legends",
  "Drinks throughout the evening",
  "Coastal-style hors d'oeuvres",
  "Silent auction participation",
  "Red carpet arrivals, photo ops, and sponsor recognition",
  "VIP networking at close",
  "Proceeds benefit the Detroit Youth Choir",
];

const details = [
  {
    q: "Is this the same as the Yacht Rock Detroit festival?",
    a: "No. This is a separate evening on Thursday, July 16 at the Roostertail. The full Yacht Rock Detroit festival runs Friday and Saturday, July 17 and 18 with 25+ live acts across the weekend.",
  },
  {
    q: "What should I wear?",
    a: "Yacht Rock Chic — summer cocktail attire. Think linen, navy, white, and a little gold. Captain hats and boat shoes welcomed.",
  },
  {
    q: "When are doors and what is the evening like?",
    a: "Red carpet arrivals begin at 6:00 PM. Cocktail hour runs until 7:00 PM, five acoustic sets play through the evening, and the silent auction closes around 9:00 PM.",
  },
  {
    q: "What does my ticket support?",
    a: "Every ticket supports the Detroit Youth Choir through Music Giving Hope, the foundation co-founded by the late Tito Jackson of the Jackson 5. The choir is a 501(c)(3) that has given Detroit kids ages 8–18 a world-class performing-arts education since 1996.",
  },
  {
    q: "Can I stay for the festival weekend?",
    a: "Yes, and you should. Festival tickets are on sale separately at yachtrockdetroit.com/tickets. Many guests turn this into a long weekend on the Detroit River.",
  },
];

const sponsors = [
  { name: "Yacht Rock Detroit", logo: "/sponsors/yrd-logo.svg", logoClass: "h-14 md:h-16" },
  { name: "Roostertail", logo: "/sponsors/roostertail-logo.svg", logoClass: "h-10 md:h-12" },
  { name: "Cambria Detroit Downtown", logo: "/cambria-logo.png", logoClass: "h-10 md:h-12" },
  { name: "GCK Productions", logo: null },
];

const MusicGivingHope = () => {
  const headerRef = useRef<HTMLElement>(null);
  const isHeaderInView = useMobileOnceInView(headerRef, { margin: "-100px" });

  usePageSEO({
    title: "Music Giving Hope Presents Yacht Rock Detroit Unplugged | July 16, 2026",
    description:
      "An exclusive charity gala benefiting the Detroit Youth Choir, presented by Music Giving Hope. Acoustic yacht rock from Peter Beckett (Player), John Ford Coley, and Walter Egan. Thursday, July 16, 2026 at The Roostertail.",
    canonical: "https://yachtrockdetroit.com/music-giving-hope",
    keywords:
      "Detroit charity gala 2026, Music Giving Hope, Detroit Youth Choir benefit, yacht rock acoustic Detroit, Roostertail charity event, Peter Beckett, John Ford Coley, Walter Egan, Detroit fundraiser July 2026",
    ogImage: "https://yachtrockdetroit.com/mgh-poster.jpg",
    jsonLd: EVENT_JSONLD,
    breadcrumbs: [
      { name: "Home", url: "https://yachtrockdetroit.com/" },
      { name: "Music Giving Hope", url: "https://yachtrockdetroit.com/music-giving-hope" },
    ],
  });

  // Indexable — explicitly allow so search engines and AI crawlers list this page.
  // Sets (not removes) the robots meta so it overrides any stale tag from a prior route.
  useEffect(() => {
    let meta = document.querySelector('meta[name="robots"]') as HTMLMetaElement | null;
    if (!meta) {
      meta = document.createElement("meta");
      meta.name = "robots";
      document.head.appendChild(meta);
    }
    meta.content = "index, follow, max-image-preview:large";
  }, []);

  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://events.ontourhq.com/ts_modal.js";
    script.async = true;
    script.onload = () => {
      (window as any).TSModals?.buildModal({
        url: "https://events.ontourhq.com/e/yrd-music-giving-hope/tickets",
        modalTriggerElementId: "mgh-modal-trigger",
      });
    };
    document.head.appendChild(script);
    return () => {
      document.head.removeChild(script);
    };
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" as ScrollBehavior });
  }, []);

  const handleGetTickets = () => {
    document.getElementById("mgh-modal-trigger")?.click();
  };

  return (
    <div className="min-h-screen bg-yacht-cream">
      <GlobalHeader
        heroRef={headerRef}
        ticketsCta={{
          label: "Unplugged Tickets",
          onClick: handleGetTickets,
        }}
      />

      <button
        id="mgh-modal-trigger"
        aria-hidden="true"
        style={{ position: "fixed", left: "-9999px", width: "1px", height: "1px", opacity: 0.01 }}
      />

      {/* ── HERO ───────────────────────────────────────────────── */}
      <section
        ref={headerRef}
        className="relative min-h-screen overflow-hidden bg-yacht-blue flex items-center"
      >
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse 90% 70% at 50% 30%, hsla(54, 93%, 60%, 0.35) 0%, transparent 55%), radial-gradient(ellipse 100% 70% at 50% 100%, hsla(54, 93%, 60%, 0.2) 0%, transparent 60%)",
          }}
        />
        <div
          className="absolute inset-0 opacity-[0.06] pointer-events-none"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
          }}
        />

        <div className="relative z-20 w-full max-w-6xl mx-auto px-6 pt-32 md:pt-56 pb-16 md:pb-24">
          <div className="grid md:grid-cols-2 gap-10 md:gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30, scale: 0.96, rotate: -3 }}
              animate={
                isHeaderInView
                  ? { opacity: 1, y: 0, scale: 1, rotate: -2 }
                  : { opacity: 0, y: 30, scale: 0.96, rotate: -3 }
              }
              transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
              className="relative mx-auto md:mx-0"
              style={{ maxWidth: "min(520px, 82vw)" }}
            >
              <div
                className="absolute -inset-8 md:-inset-12 rounded-full blur-3xl pointer-events-none"
                style={{
                  background:
                    "radial-gradient(circle, hsla(54, 93%, 65%, 0.45) 0%, hsla(193, 52%, 60%, 0.25) 50%, transparent 75%)",
                }}
              />
              <img
                src={posterImage}
                alt="Music Giving Hope Presents Yacht Rock Detroit Unplugged. A charity gala benefiting the Detroit Youth Choir. Thursday, July 16, 2026 at The Roostertail. Featuring Peter Beckett, John Ford Coley, and Walter Egan."
                className="relative z-10 w-full h-auto block rounded-lg ring-4 ring-white/80"
                style={{
                  boxShadow:
                    "0 30px 80px -20px rgba(0,0,0,0.45), 0 12px 35px -15px rgba(0,0,0,0.35)",
                }}
                loading="eager"
              />
            </motion.div>

            <div className="text-center md:text-left">
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={isHeaderInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                transition={{ duration: 0.7, delay: 0.3 }}
                className="text-yacht-yellow text-xs md:text-sm font-bold tracking-[0.3em] uppercase mb-4"
              >
                Music Giving Hope Presents
              </motion.p>

              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={isHeaderInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                transition={{ duration: 0.7, delay: 0.4 }}
                className="text-white text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.05] mb-6"
              >
                Yacht Rock Detroit<br />
                <span className="italic text-yacht-yellow">Unplugged</span>
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={isHeaderInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                transition={{ duration: 0.7, delay: 0.5 }}
                className="text-white/85 text-base md:text-lg leading-relaxed mb-7 max-w-md mx-auto md:mx-0"
              >
                An exclusive charity gala benefiting the Detroit Youth Choir — an intimate evening of acoustic yacht rock, cocktails, and community impact on the Detroit River.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={isHeaderInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                transition={{ duration: 0.7, delay: 0.6 }}
                className="flex flex-col gap-2.5 text-white/90 text-sm md:text-base mb-6 items-center md:items-start"
              >
                <span className="flex items-center gap-2.5">
                  <Calendar className="w-4 h-4 md:w-5 md:h-5 text-yacht-yellow flex-shrink-0" />
                  Thursday, July 16, 2026
                </span>
                <span className="flex items-center gap-2.5">
                  <Clock className="w-4 h-4 md:w-5 md:h-5 text-yacht-yellow flex-shrink-0" />
                  6:00 to 9:30 PM
                </span>
                <span className="flex items-center gap-2.5">
                  <MapPin className="w-4 h-4 md:w-5 md:h-5 text-yacht-yellow flex-shrink-0" />
                  The Roostertail, Detroit
                </span>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={isHeaderInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                transition={{ duration: 0.7, delay: 0.75 }}
                className="mb-6 max-w-md mx-auto md:mx-0"
              >
                <p className="text-yacht-yellow text-[10px] md:text-xs font-bold tracking-[0.25em] uppercase mb-2">
                  Your $100 ticket includes
                </p>
                <p className="text-white/90 text-sm md:text-base leading-relaxed">
                  Five live acoustic sets <span className="text-yacht-yellow/70">·</span> Drinks all evening <span className="text-yacht-yellow/70">·</span> Coastal-style hors d'oeuvres <span className="text-yacht-yellow/70">·</span> Silent auction <span className="text-yacht-yellow/70">·</span> Red carpet
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={isHeaderInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                transition={{ duration: 0.7, delay: 0.8 }}
                className="flex flex-col items-center md:items-start gap-3"
              >
                <button
                  onClick={handleGetTickets}
                  className="bg-yacht-yellow text-yacht-charcoal font-bold px-10 py-4 md:py-5 rounded-full text-base md:text-lg uppercase tracking-wider hover:brightness-110 transition-all cursor-pointer"
                  style={{ boxShadow: "0 14px 40px -8px hsla(54, 93%, 49%, 0.55)" }}
                >
                  Get Tickets · $100
                </button>
                <p className="text-white/65 text-xs tracking-wider uppercase">
                  Limited to 500 guests
                </p>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* ── THE CAUSE (Detroit Youth Choir + Music Giving Hope) ── */}
      <section className="py-20 md:py-28 px-6 bg-yacht-cream">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-yacht-blue text-sm font-semibold tracking-[0.25em] uppercase mb-4">
              Why We're Here
            </p>
            <h2 className="text-h1 md:text-hero text-yacht-charcoal font-bold mb-7 leading-[1.05]">
              The sound they built in the &rsquo;70s.
              <br />
              <span className="relative inline-block">
                <span className="relative z-10">The kids who&rsquo;ll carry it.</span>
                <motion.span
                  aria-hidden="true"
                  initial={{ scaleX: 0 }}
                  whileInView={{ scaleX: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 1.1, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
                  className="absolute left-0 right-0 bottom-[0.08em] h-[0.18em] bg-yacht-yellow origin-left rounded-sm -z-0"
                  style={{ mixBlendMode: "multiply" }}
                />
              </span>
            </h2>
            <p className="text-lg md:text-xl text-yacht-charcoal/75 leading-relaxed max-w-2xl mx-auto">
              Music Giving Hope &mdash; the foundation co-founded by the late Tito Jackson of the Jackson 5 &mdash; uses music to change lives. This gala benefits the Detroit Youth Choir, the nationally recognized 501(c)(3) that has given Detroit kids ages 8 to 18 a world-class performing-arts home since 1996 (you may know them from America&rsquo;s Got Talent). For one night, the artists who built yacht rock in the 1970s play it acoustic on the river &mdash; so the next generation of Detroit performers can keep singing.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ── WHAT'S INCLUDED ────────────────────────────────────── */}
      <section className="py-20 md:py-28 px-6 bg-yacht-blue text-white relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.1] pointer-events-none"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
          }}
        />
        <div className="relative z-10 max-w-4xl mx-auto">
          <motion.div
            className="text-center mb-12 md:mb-14"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-yacht-yellow text-sm font-semibold tracking-[0.25em] uppercase mb-3">
              $100 · Yacht Rock Chic Attire
            </p>
            <h2 className="text-h1 md:text-hero font-bold leading-tight">
              What's in the evening.
            </h2>
          </motion.div>

          <div className="max-w-2xl mx-auto">
            <ul className="divide-y divide-white/10 border-t border-b border-white/10">
              {includes.map((item, i) => (
                <motion.li
                  key={i}
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className="flex items-baseline gap-4 md:gap-6 py-5"
                >
                  <span className="text-yacht-yellow font-bold text-sm md:text-base tracking-[0.2em] w-10 flex-shrink-0">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="text-white/90 text-base md:text-lg leading-snug">
                    {item}
                  </span>
                </motion.li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ── PERFORMERS ─────────────────────────────────────────── */}
      <section className="py-20 md:py-28 px-6 bg-yacht-charcoal text-white relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.04] pointer-events-none"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
          }}
        />
        <div className="relative z-10 max-w-6xl mx-auto">
          <motion.div
            className="text-center mb-14 md:mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-yacht-yellow text-sm font-semibold tracking-[0.25em] uppercase mb-4">
              Acoustic · Up close · Unplugged
            </p>
            <h2 className="text-h1 md:text-hero font-bold leading-[1.05]">
              The voices behind<br className="hidden md:block" /> the songs you know by heart.
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-10 md:gap-12">
            {performers.map((performer, i) => (
              <motion.div
                key={performer.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.6, delay: i * 0.12 }}
                className="flex flex-col items-center text-center"
              >
                <div className="relative mb-6">
                  <div
                    className="absolute -inset-3 rounded-full blur-2xl pointer-events-none"
                    style={{
                      background:
                        "radial-gradient(circle, hsla(54, 93%, 60%, 0.25) 0%, transparent 70%)",
                    }}
                  />
                  <img
                    src={performer.image}
                    alt={performer.name}
                    className="relative z-10 w-36 h-36 md:w-44 md:h-44 rounded-full object-cover"
                    style={{ boxShadow: "0 16px 40px -12px rgba(0,0,0,0.6)" }}
                    loading="lazy"
                  />
                </div>

                <div className="flex items-center gap-1.5 text-yacht-yellow mb-3" aria-hidden="true">
                  <svg className="w-3.5 h-3.5 fill-current" viewBox="0 0 20 20"><path d="M10 1l2.6 6.3L19 8l-5 4.8 1.4 6.7L10 16l-5.4 3.5L6 12.8 1 8l6.4-.7z" /></svg>
                  <svg className="w-3.5 h-3.5 fill-current" viewBox="0 0 20 20"><path d="M10 1l2.6 6.3L19 8l-5 4.8 1.4 6.7L10 16l-5.4 3.5L6 12.8 1 8l6.4-.7z" /></svg>
                  <svg className="w-3.5 h-3.5 fill-current" viewBox="0 0 20 20"><path d="M10 1l2.6 6.3L19 8l-5 4.8 1.4 6.7L10 16l-5.4 3.5L6 12.8 1 8l6.4-.7z" /></svg>
                  <svg className="w-3.5 h-3.5 fill-current" viewBox="0 0 20 20"><path d="M10 1l2.6 6.3L19 8l-5 4.8 1.4 6.7L10 16l-5.4 3.5L6 12.8 1 8l6.4-.7z" /></svg>
                  <svg className="w-3.5 h-3.5 fill-current" viewBox="0 0 20 20"><path d="M10 1l2.6 6.3L19 8l-5 4.8 1.4 6.7L10 16l-5.4 3.5L6 12.8 1 8l6.4-.7z" /></svg>
                </div>

                <h3 className="text-2xl md:text-3xl font-bold mb-1.5">{performer.name}</h3>
                {performer.band && (
                  <p className="text-white/55 text-sm tracking-wide mb-4">
                    {performer.band}
                  </p>
                )}
                <p
                  className="text-white/90 text-base md:text-lg mb-4"
                  style={{ fontFamily: "Georgia, Times New Roman, serif" }}
                >
                  &ldquo;{performer.song}&rdquo;
                </p>
                <p className="text-white/65 text-sm leading-relaxed max-w-[28ch]">{performer.blurb}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── SILENT AUCTION ─────────────────────────────────────── */}
      <section className="py-20 md:py-28 px-6 bg-yacht-cream overflow-hidden">
        <div className="max-w-6xl mx-auto">
          <motion.div
            className="text-center mb-10 md:mb-14"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-block bg-yacht-blue text-white text-xs font-semibold tracking-[0.2em] uppercase px-3 py-1 rounded-full mb-5">
              Silent Auction
            </span>
            <h2 className="text-yacht-charcoal text-3xl md:text-5xl font-semibold leading-[1.1] mb-4">
              Own a piece of rock history.
            </h2>
            <p className="text-yacht-charcoal/70 text-base md:text-lg max-w-xl mx-auto leading-relaxed">
              Signed guitars and one-of-a-kind memorabilia from the legends themselves.
              Bidding opens at 6:00 PM and closes at 9:00 PM — and every winning bid
              supports the Detroit Youth Choir.
            </p>
          </motion.div>

          <div
            className="flex gap-5 overflow-x-auto snap-x snap-mandatory pb-4 -mx-6 px-6 md:mx-0 md:px-0 md:pb-0 md:flex-wrap md:justify-center md:gap-10 md:overflow-visible"
            style={{ WebkitOverflowScrolling: "touch" }}
          >
            {auctionItems.map((auction, i) => (
              <motion.div
                key={auction.file}
                initial={{ opacity: 0, y: 25 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                className="snap-center flex-shrink-0 w-[75vw] max-w-[320px] md:w-[calc(33.333%-1.7rem)] md:max-w-none"
              >
                <a
                  href={auction.full}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`View the ${auction.name} auction item full size`}
                  className="block group"
                >
                  <img
                    src={auction.preview}
                    alt={`${auction.name} silent auction item at the Music Giving Hope Yacht Rock Detroit charity gala — ${auction.item}`}
                    className="w-full h-auto block rounded-lg group-hover:scale-[1.02] transition-transform duration-300"
                    style={{ boxShadow: "0 18px 45px -14px rgba(0,0,0,0.4)" }}
                    loading="lazy"
                  />
                </a>
                <div className="text-center mt-4 px-2">
                  <h3 className="text-yacht-charcoal font-bold text-lg md:text-xl mb-1">
                    {auction.name}
                  </h3>
                  <p className="text-yacht-charcoal/65 text-sm leading-relaxed">
                    {auction.item}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>

          <p className="md:hidden text-center text-yacht-charcoal/50 text-xs tracking-widest uppercase mt-2 mb-8">
            Swipe to see all five &rarr;
          </p>

          <motion.div
            className="text-center mt-6 md:mt-14"
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <p className="text-yacht-charcoal/70 text-base md:text-lg mb-5">
              You have to be in the room to win one.
            </p>
            <button
              onClick={handleGetTickets}
              className="bg-yacht-yellow text-yacht-charcoal font-bold px-9 py-3.5 rounded-full text-sm md:text-base uppercase tracking-wider hover:brightness-110 transition-all cursor-pointer"
              style={{ boxShadow: "0 12px 32px -8px hsla(54, 93%, 49%, 0.5)" }}
            >
              Get Tickets · $100
            </button>
          </motion.div>
        </div>
      </section>

      {/* ── TIMELINE (Faq5-style) ──────────────────────────────── */}
      <section className="py-24 md:py-32 px-6 bg-yacht-cream">
        <div className="max-w-3xl mx-auto">
          <motion.div
            className="text-center mb-14"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-block bg-yacht-blue text-white text-xs font-semibold tracking-[0.2em] uppercase px-3 py-1 rounded-full mb-5">
              Your Evening
            </span>
            <h2 className="text-yacht-charcoal text-3xl md:text-5xl font-semibold leading-[1.1] mb-4">
              From 6:00 to 9:30 on the river.
            </h2>
            <p className="text-yacht-charcoal/70 text-base md:text-lg max-w-xl mx-auto leading-relaxed">
              A red carpet, five live sets, a silent auction, and a reason to be there.
            </p>
          </motion.div>

          <div className="mx-auto max-w-2xl">
            {timeline.map((item, i) => (
              <motion.div
                key={item.time}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.4, delay: i * 0.04 }}
                className="mb-8 flex gap-4"
              >
                <span className="flex size-7 shrink-0 items-center justify-center rounded-sm bg-yacht-blue/10 font-mono text-xs text-yacht-blue font-semibold">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <div>
                  <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 mb-1">
                    <span className="text-yacht-blue font-bold text-sm tracking-wider">
                      {item.time}
                    </span>
                    <h3 className="text-yacht-charcoal font-semibold text-base md:text-lg">
                      {item.title}
                    </h3>
                  </div>
                  <p className="text-sm md:text-base text-yacht-charcoal/65 leading-relaxed">
                    {item.detail}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── DETAILS (Faq5-style) ───────────────────────────────── */}
      <section className="py-24 md:py-32 px-6 bg-yacht-cream">
        <div className="max-w-3xl mx-auto">
          <motion.div
            className="text-center mb-14"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-block bg-yacht-blue text-white text-xs font-semibold tracking-[0.2em] uppercase px-3 py-1 rounded-full mb-5">
              The Details
            </span>
            <h2 className="text-yacht-charcoal text-3xl md:text-5xl font-semibold leading-[1.1] mb-4">
              What to know before the night.
            </h2>
            <p className="text-yacht-charcoal/70 text-base md:text-lg max-w-xl mx-auto leading-relaxed">
              The short answers to the questions guests ask most.
            </p>
          </motion.div>

          <div className="mx-auto max-w-2xl">
            {details.map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.4, delay: i * 0.04 }}
                className="mb-8 flex gap-4"
              >
                <span className="flex size-7 shrink-0 items-center justify-center rounded-sm bg-yacht-blue/10 font-mono text-xs text-yacht-blue font-semibold">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <div>
                  <h3 className="text-yacht-charcoal font-semibold text-base md:text-lg mb-2">
                    {item.q}
                  </h3>
                  <p className="text-sm md:text-base text-yacht-charcoal/70 leading-relaxed">
                    {item.a}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CROSS-PROMO: YRD WEEKEND ───────────────────────────── */}
      <section className="py-16 md:py-24 px-6 bg-yacht-cream">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative rounded-3xl overflow-hidden bg-yacht-blue p-8 md:p-14"
            style={{ boxShadow: "0 20px 60px -15px hsla(193, 52%, 47%, 0.35)" }}
          >
            <img
              src={PHOTOS.CROWD.sunsetStageView}
              alt=""
              className="absolute inset-0 w-full h-full object-cover opacity-20"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-br from-yacht-blue/95 via-yacht-blue/90 to-yacht-blue/80" />

            <div className="relative z-10">
              <p className="text-yacht-yellow text-xs font-bold tracking-[0.3em] uppercase mb-3">
                Staying for the Weekend?
              </p>
              <h2 className="text-white text-3xl md:text-4xl lg:text-5xl font-bold leading-tight mb-3">
                Yacht Rock Detroit<br className="hidden md:block" />
                <span className="italic text-yacht-yellow">July 17 & 18, 2026</span>
              </h2>
              <p className="text-white/80 text-base md:text-lg max-w-xl mb-5 leading-relaxed">
                25+ live acts across two days. Three Dog Night, Pablo Cruise, Ambrosia, Yacht Rock Revue and more. Same waterfront, bigger stage. Festival tickets are public and on sale now.
              </p>
              <div className="flex flex-wrap gap-3">
                <a
                  href="/tickets"
                  className="inline-flex items-center gap-2 bg-yacht-yellow text-yacht-charcoal font-bold px-7 py-3 rounded-full text-sm md:text-base uppercase tracking-wide hover:brightness-110 transition-all"
                >
                  Festival Tickets
                  <ArrowRight className="w-4 h-4" />
                </a>
                <a
                  href="/lineup"
                  className="inline-flex items-center gap-2 bg-white/10 border border-white/20 text-white font-semibold px-7 py-3 rounded-full text-sm md:text-base uppercase tracking-wide hover:bg-white/20 transition-all"
                >
                  See the Lineup
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── SPONSORS ───────────────────────────────────────────── */}
      <section className="py-14 md:py-20 px-6 bg-yacht-cream border-t border-yacht-blue/10">
        <div className="max-w-5xl mx-auto text-center">
          <p className="text-yacht-blue text-xs font-bold tracking-[0.3em] uppercase mb-6">
            Sponsored By
          </p>
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-12">
            {sponsors.map((s) =>
              s.logo ? (
                <img
                  key={s.name}
                  src={s.logo}
                  alt={s.name}
                  className={`${s.logoClass} w-auto object-contain`}
                  loading="lazy"
                />
              ) : (
                <span
                  key={s.name}
                  className="text-yacht-charcoal/80 text-base md:text-lg font-semibold tracking-wide"
                  style={{ fontFamily: "Georgia, Times New Roman, serif" }}
                >
                  {s.name}
                </span>
              )
            )}
          </div>
        </div>
      </section>

      {/* ── FINAL CTA ──────────────────────────────────────────── */}
      <section className="py-20 md:py-28 px-6 bg-yacht-yellow text-yacht-charcoal relative overflow-hidden">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse 70% 60% at 50% 30%, hsla(45, 100%, 95%, 0.5) 0%, transparent 60%)",
          }}
        />

        <div className="relative z-10 max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-h1 md:text-hero font-bold mb-5 leading-tight">
              Thursday, July 16.<br />
              Before the festival kicks off.
            </h2>
            <p className="text-yacht-charcoal/80 text-lg md:text-xl mb-10 leading-relaxed">
              The Roostertail · $100 a ticket · limited to 500. Every ticket lifts the Detroit Youth Choir.
            </p>

            <button
              onClick={handleGetTickets}
              className="bg-yacht-charcoal text-white font-bold px-12 py-5 rounded-full text-lg md:text-xl uppercase tracking-wide hover:bg-black transition-all cursor-pointer"
              style={{ boxShadow: "0 14px 40px -8px rgba(0,0,0,0.35)" }}
            >
              Get Tickets
            </button>

            <p className="text-yacht-charcoal/60 text-xs mt-6 tracking-widest uppercase">
              Yacht Rock Chic · 21+ · Doors 6:00 PM
            </p>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default MusicGivingHope;
